import { useQuery } from '@tanstack/react-query';

import { useT } from '@/i18n/context';
import { fetchHealth } from '@/lib/api';

/**
 * Scaffold page: proves the SPA, nginx routing and the API all line up.
 * Replaced by the camera screen in the next step.
 */
export function HealthPage() {
  const t = useT();
  const { data, isPending, error } = useQuery({
    queryKey: ['health'],
    queryFn: fetchHealth,
    refetchInterval: 15_000,
  });

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 p-6">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">Selflapse</h1>
        <p className="text-sm opacity-60">{t('health.subtitle')}</p>
      </header>

      {isPending && <p className="text-sm opacity-60">{t('health.checking')}</p>}

      {error && (
        <p className="rounded-lg bg-red-500/10 p-4 text-sm text-red-500">
          {t('health.unreachable', { message: error.message })}
        </p>
      )}

      {data && (
        <section className="flex flex-col gap-3">
          <div className="flex items-baseline justify-between">
            <span className="font-medium">
              {data.status === 'ok' ? t('health.ok') : t('health.degraded')}
            </span>
            <span className="text-xs opacity-50">
              {data.env} · {data.version}
            </span>
          </div>

          <ul className="divide-y divide-current/10 overflow-hidden rounded-lg bg-current/5">
            {Object.entries(data.checks).map(([name, check]) => (
              <li key={name} className="flex items-center justify-between p-3 text-sm">
                <span className="capitalize">{name}</span>
                <span
                  className={
                    check.status === 'ok'
                      ? 'text-emerald-500'
                      : 'max-w-[60%] truncate text-red-500'
                  }
                  title={check.error}
                >
                  {check.status === 'ok'
                    ? `ok · ${check.latency_ms ?? 0}ms`
                    : check.error}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
